import {useTranslation} from "tools/i18n";
import {Accordion, AccordionDetails, AccordionSummary, Typography} from "@material-ui/core";
import {makeStyles} from "@material-ui/styles";
import React, {useContext, useState} from "react";
import ProductSpecs from "./specs";
import Link from "next/link";
import parsePrices from "functions/parseSWPrices";
import ExpandMore from "../../../public/images/icons/expand_more_light.svg";
import {TrackingContext} from "../../providers/tracking";
import useSessionStore from '@services/stores/SessionStore';
import ProductBundle from '@components/page/product/partial/ProductBundle';

const useStyles = makeStyles(theme => ({
	wrapper: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		marginTop: 30,
		paddingBottom: 40,
		[theme.breakpoints.up('sm')]: {
			marginTop: 30,
			flexDirection: 'row',
			justifyContent: 'space-between',
			gap: 30,
			backgroundColor: theme.palette.primary.background
		}
	},
	accordion: {
		width: '100%',
		boxShadow: 'none',
		borderTop: '1px solid #ccc',
		backgroundColor: theme.palette.primary.background,
		color: theme.palette.primary.color,
		'&:before': {
			display: 'none'
		},
		[theme.breakpoints.down('sm')]: {
			backgroundColor: theme.palette.primary.background
		},
		[theme.breakpoints.up('sm')]: {
			'&.product-info': {
				width: 'calc(60% - 15px)'
			}
		}
	},
	accordionExpanded: {
		margin: '0 0 25px 0 !important'
	},
	summary: {
		margin: '0 !important',
		padding: '5px 0',
		[theme.breakpoints.up('sm')]: {
			padding: '10px 0',
			minHeight: '0 !important',
			marginBottom: '0 !important'
		}
	},
	summaryContent: {
		margin: 0,
		padding: '15px 0'
	},
	summaryText: {
		fontSize: 15,
		fontWeight: 300,
		letterSpacing: 0,
		color: theme.palette.primary.color,
    	textTransform: 'uppercase',
		[theme.breakpoints.down('sm')]: {
			fontSize: 14
		}
	},
	summaryIcon: {
		height: 28,
		padding: 0,
		marginRight: 0,
		'& svg': {
			transform: 'scale(0.8)',
			fill: '#aaa'
		}
	},
	detailsText: {
		fontSize: 15,
		fontWeight: 300,
		color: theme.palette.primary.color,
		lineHeight: 1.5,
		letterSpacing: 0,
		whiteSpace: 'pre-wrap',
		[theme.breakpoints.down('sm')]: {
			fontSize: 14
		}
	},
	detailsRoot: {
		display: 'flex',
		flexDirection: 'column',
		padding: 0
	},
	additionalWrapper: {
		[theme.breakpoints.up('sm')]: {
			width: 'calc(40% - 15px)'
		}
	},
	description: {
		margin: 0,
		fontSize: 15,
		fontWeight: 300,
		lineHeight: 1.5,
		letterSpacing: 0,
		color: theme.palette.primary.color,
		'& p:first-of-type': {
			marginTop: 0
		},
		'& p:not(:first-of-type)': {
			padding: 0
		},
		'&.outlet': {
			fontStyle: 'italic',
			'& > span': {
				fontWeight: 'bold'
			}
		},
		'&.slim-aarons': {
			marginTop: 21,
			whiteSpace: 'break-spaces'
		},
		[theme.breakpoints.down('sm')]: {
			fontSize: 14
		}
	},
	discover: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		padding: '22px 0',
		borderTop: '1px solid #ccc',
		'& label': {
			fontSize: 15,
			fontWeight: 300,
			letterSpacing: 0.5,
			color: theme.palette.primary.color,
			textTransform: 'uppercase',
			whiteSpace: 'nowrap',
			[theme.breakpoints.down('sm')]: {
				fontSize: 14
			}
		}
	},
	discoverList: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: 10,
		margin: 0,
		padding: '0 0 0 10px',
		listStyle: 'none',
		'& li': {
			display: 'flex',
			'& a': {
				padding: '6px 8px',
				fontSize: 15,
				fontWeight: 300,
				lineHeight: '12px',
				textDecoration: 'none',
				color: 'inherit',
				border: '1px solid #ccc',
				[theme.breakpoints.down('sm')]: {
					padding: '6px 8px',
					fontSize: 14,
				}
			}
		}
	},
	specialCare: {
		'& a': {
			display: 'inline',
			//margin: '1em 0 0',
		}
	}
}), {name: 'ProductAdditional'});

export default function ProductAdditional({product, menu, productFamilyArticle}) {
	const formatPrice = useSessionStore((state) => state.methods.formatPrice);
	const classes = useStyles();
	const {t} = useTranslation('product-info');
	const config = useSessionStore((state) => state.session.config);
	const {actions: trackingActions} = useContext(TrackingContext);
	const [state, setState] = useState({info: true, ar3d: false, slimAarons: false, delivery: false, care: false, price: false});
	const careInstructionPropertyGroupId = 'c09eb1c388fd55519afde850e5c689a5';
	const prices = product.calculatedPrices && product.calculatedPrices.length > 0 ? product.calculatedPrices[0] : product.calculatedPrice;
	const isOutlet = product.translated?.customFields?.is_outlet_product || false;

	function updateAccordionState(accordion) {
		let newState = {...state};
		newState[accordion] = !state[accordion];

		setState(newState);
	}

	function findCategory(categoryId) {
		if(menu) {
			let foundCategory = recursiveFind(menu, categoryId);

			return foundCategory;
		} else {
			return false;
		}
	}

	function recursiveFind(items, categoryId) {
		let foundCategory = false;

		items.forEach((item) => {
			if(item.id === categoryId) {
				foundCategory = item;
			} else {
				let childrenFound = recursiveFind(item.children, categoryId);

				if(childrenFound) {
					foundCategory = childrenFound;
				}
			}
		});

		return foundCategory;
	}

	function getDiscoverCategories() {
		let categoryIds = product.categoryIds;
		let mainCategory = product.seoCategory?.id;
		let categories = [];

		if(mainCategory && categoryIds) {
			categoryIds.sort((a,b) => {
				if(a === mainCategory) {
					return 1;
				} else if(b === mainCategory) {
					return -1;
				} else {
					return 0;
				}
			});
		}

		let foundCategories = 0;
		if(categoryIds) {
			categoryIds.forEach((categoryId, index) => {
				let category = findCategory(categoryId);

				if(category && foundCategories < 2) {
					categories.push(category);
					foundCategories++;
				}
			});
		}

		return categories;
	}

	function getCheapPrice() {
		let calculatedPrice = product.calculatedPrices && product.calculatedPrices.length > 0 ? product.calculatedPrices[0] : product.calculatedPrice;
		const prices = parsePrices(calculatedPrice);

		return prices.regulationPrice;
	}

	function getCareInstructions() {
		let properties = product.properties || [];
		let filteredProperties = properties.filter((property) => property.groupId === careInstructionPropertyGroupId);

		if(filteredProperties && filteredProperties.length > 0) {
			let careInstructionProperty = filteredProperties[0];
			let careInstructionTranslation = careInstructionProperty.translated?.name || careInstructionProperty.name;

			if(t('product-info:care-instructions-' + careInstructionTranslation) !== 'care-instructions-' + careInstructionTranslation) {
				if (t('product-info:care-instructions-' + careInstructionTranslation).length > 0) {
					return t('product-info:care-instructions-' + careInstructionTranslation);
				}
			}
		}

		if(product.seoCategory?.translated.customFields?.care_instruction) {
			return product.seoCategory?.translated.customFields?.care_instruction;
		}

		return t('product-info:care-content');
	}

	function isEichholtsOutlet() {
		const manufacturer = product.manufacturer;
		return manufacturer?.translated?.name === 'Eichholtz';
	}

    function getProductDeliveryInfo() {
        const deliveryType = product?.customFields?.delivery_package_type_code
        const deliveryTime = product?.deliveryTime?.id

        /* TYPE STUFF */
        let type = ''
        if (deliveryType === 'package') {
            type = t('product-info:delivery-content-type-package', {currencySymbol: config.language.locale === 'en' && config.currency === 'USD' ? '$' : '€'});
        }

        if (deliveryType === 'eur_pallet' || deliveryType === 'special_pallet') {
            type = t('product-info:delivery-content-type-bulky', {currencySymbol: config.language.locale === 'en' && config.currency === 'USD' ? '$' : '€'});
        }

        /* TIME STUFF */
        let time = ''

        switch (deliveryTime) {
            // I LAGER FÖR OMÅGENDE LEVERANS
            case '5ae66ca4e2124856b52e872650f1de21':
                time = `${t('product-info:delivery-content-time-in-stock')}`;
                break;

            // SKICKAS INOM X-X VECKOR
            case '01975f518d3079639776346cca397561':
            case '01975f539b9c7d15a011ab30982896ca':
            case '01975f530e3475bfb6e571ab1a2739fb':
            case '4a26ed934de94942865763e63a2e6efa':
                const regexMatch = product?.deliveryTime?.translated?.name.match(/\d+\s*[-–]\s*\d+/);
                const regexTime = regexMatch ? regexMatch[0].replace(/\s*/g, '') : '';

                if (product.manufacturerId === '34ccd598c93756c6af59a1d6ff4f15bf') {
                    time = `${t('product-info:delivery-content-time-incoming-internal', { time: regexTime })}`;
                } else {
                    time = `${t('product-info:delivery-content-time-incoming-external', {
                        manufacturer: product?.manufacturer?.translated?.name,
                        time: regexTime
                    })}`;
                }
                break;

            // BESTÄLLNINGSVARA
            case '45a286a87e28491b8c4802d0b395712d':
                if (product.manufacturerId === '34ccd598c93756c6af59a1d6ff4f15bf') {
                    time = `${t('product-info:delivery-content-time-toorder-internal')}`;
                } else {
                    time = `${t('product-info:delivery-content-time-toorder-external', {
                        manufacturer: product?.manufacturer?.translated?.name
                    })}`;
                }
                break;

            // SLUTSÅLD or SLUT FÖR SÄSONG
            case '019257c5169f7d40983b28a28c4e72a3':
            case '0192ae399fe970fc8c2a32844620bbec':
                time = `${t('product-info:delivery-content-time-out-of-stock')}`;
                break;

            default:
                time = product?.deliveryTime?.translated?.name + '.'
                break;
        }

        return `${time}\n\n${type}`
    }

	return (
		<div className={classes.wrapper}>
			<Accordion classes={{root: [classes.accordion, 'product-info'].join(' '), expanded: classes.accordionExpanded}} square={true} expanded={state.info} onChange={() => updateAccordionState('info')}>
				<AccordionSummary classes={{root: classes.summary, content: classes.summaryContent, expanded: classes.summary, expandIcon: classes.summaryIcon}} expandIcon={<ExpandMore width={48}/>}>
					<Typography className={classes.summaryText}>{t('product-info:product-information')}</Typography>
				</AccordionSummary>
				<AccordionDetails classes={{root: classes.detailsRoot}}>
					<div className={classes.description} dangerouslySetInnerHTML={{__html: product.translated.description}}></div>
					<ProductBundle product={product} />
					{product?.manufacturer?.translated?.name === 'Slim Aarons' ? <div className={[classes.description, 'slim-aarons'].join(' ')}>{t('product-info:slim-aarons-additional-description')}</div> : ''}
					{isOutlet ? <div className={[classes.description, 'outlet'].join(' ')} dangerouslySetInnerHTML={{__html: isEichholtsOutlet() ? t('is-outlet-eichholtz') : t('is-outlet')}} /> : ''}
					<ProductSpecs product={product} productFamilyArticle={productFamilyArticle}/>
				</AccordionDetails>
			</Accordion>

			<div className={classes.additionalWrapper}>
				{product?.manufacturer?.translated?.name === 'Slim Aarons' ? <Accordion classes={{root: [classes.accordion, 'slim-aarons'].join(' '), expanded: classes.accordionExpanded}} square={true} expanded={state.slimAarons} onChange={() => {
					if(!state.delivery) {
						trackingActions.click({
							type: 'product_section_open',
							text: product?.manufacturer?.translated?.name,
							identifier: 'slim-aarons'
						});
					}

					updateAccordionState('slimAarons');
				}}>
					<AccordionSummary classes={{root: classes.summary, content: classes.summaryContent, expanded: classes.summary, expandIcon: classes.summaryIcon}} expandIcon={<ExpandMore width={48}/>}>
						<Typography className={classes.summaryText}>{product?.manufacturer?.translated?.name}</Typography>
					</AccordionSummary>
					<AccordionDetails classes={{root: classes.detailsRoot}}>
						<Typography className={[classes.detailsText, 'slim-aarons'].join(' ')}>{t('product-info:slim-aarons-general')}</Typography>
					</AccordionDetails>
				</Accordion> : '' }

				<Accordion classes={{root: [classes.accordion, 'delivery'].join(' '), expanded: classes.accordionExpanded}} square={true} expanded={state.delivery} onChange={() => {
					if(!state.delivery) {
						trackingActions.click({
							type: 'product_section_open',
							text: t('product-info:delivery-title'),
							identifier: 'delivery'
						});
					}

					updateAccordionState('delivery');
				}}>
					<AccordionSummary classes={{root: classes.summary, content: classes.summaryContent, expanded: classes.summary, expandIcon: classes.summaryIcon}} expandIcon={<ExpandMore width={48}/>}>
						<Typography className={classes.summaryText}>{t('product-info:delivery-title')}</Typography>
					</AccordionSummary>
					<AccordionDetails classes={{root: classes.detailsRoot}}>
						{product?.manufacturer?.translated?.name === 'Slim Aarons' ?
							<Typography className={[classes.detailsText, 'slim-aarons'].join(' ')}>{t('product-info:slim-aarons-shipping')}</Typography>
                            :
                            <Typography className={classes.detailsText} dangerouslySetInnerHTML={{__html: getProductDeliveryInfo()}}></Typography>
                        }
					</AccordionDetails>
				</Accordion>

				{getCareInstructions() ? <Accordion classes={{root: [classes.accordion, 'care'].join(' '), expanded: classes.accordionExpanded}} square={true} expanded={state.care} onChange={() => {
					if(!state.care) {
						trackingActions.click({
							type: 'product_section_open',
							text: t('product-info:care-title'),
							identifier: 'care'
						});
					}

					updateAccordionState('care');
				}}>
					<AccordionSummary classes={{root: classes.summary, content: classes.summaryContent, expanded: classes.summary, expandIcon: classes.summaryIcon}} expandIcon={<ExpandMore width={48}/>}>
						<Typography className={classes.summaryText}>{t('product-info:care-title')}</Typography>
					</AccordionSummary>
					<AccordionDetails classes={{root: classes.detailsRoot}}>
						<Typography className={[classes.detailsText, classes.specialCare].join(' ')} dangerouslySetInnerHTML={{__html: getCareInstructions()}}></Typography>
					</AccordionDetails>
				</Accordion> : ''}

				{getDiscoverCategories().length > 0 ? <div className={classes.discover}>
					<label>{t('discover-more')}:</label>
					<ul className={classes.discoverList}>
						{getDiscoverCategories().map((category, index) => {
							return <li key={index}>
								<Link href={category.url}>
									<a onClick={() => trackingActions.click({type: 'product_discover_link', text: category.name, identifier: category.url})}>{category.name}</a>
								</Link>
							</li>
						})}
					</ul>
				</div> : ''}

				{prices.listPrice ? <Accordion classes={{root: [classes.accordion, 'price'].join(' '), expanded: classes.accordionExpanded}} square={true} expanded={state.price} onChange={() => {
					if(!state.price) {
						trackingActions.click({
							type: 'product_section_open',
							text: t('product-info:price-history'),
							identifier: 'price'
						});
					}

					updateAccordionState('price');
				}}>
					<AccordionSummary classes={{root: classes.summary, content: classes.summaryContent, expanded: classes.summary, expandIcon: classes.summaryIcon}} expandIcon={<ExpandMore width={48}/>}>
						<Typography className={classes.summaryText}>{t('product-info:price-history')}</Typography>
					</AccordionSummary>
					<AccordionDetails classes={{root: classes.detailsRoot}}>
						<Typography className={classes.detailsText}>{t('attributes:cheapest-price', {price: formatPrice(getCheapPrice())})}</Typography>
					</AccordionDetails>
				</Accordion> : ''}
			</div>
		</div>
	);
}
